import { Flex, HStack, Text } from '@chakra-ui/react';
import {
  ContactType,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import { useSearchParams } from 'react-router-dom';
import { useNotificationsContext } from '../../context/NotificationsContext';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterSelectProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

const darkSelectStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    pr: 4,
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    fontSize: 'sm',
    _hover: { bg: 'whiteAlpha.100' },
  }),
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: FilterSelectProps) => {
  const selectedOption = options.find((opt) => opt.value === value) ?? null;

  return (
    <Flex alignItems="center" cursor="pointer" gap={2} px={2} py={1}>
      <Select<FilterOption>
        chakraStyles={darkSelectStyles}
        components={{
          SingleValue: (props) => {
            return (
              <Text color="white" fontSize="sm" gridArea="1/1/2/3">
                {label}: {props.data.label}
              </Text>
            );
          },
        }}
        getOptionLabel={(opt) => opt.label}
        getOptionValue={(opt) => opt.value}
        isSearchable={false}
        options={options}
        size="sm"
        value={selectedOption}
        onChange={(newValue) => {
          if (newValue) {
            onChange(newValue.value);
          }
        }}
      />
    </Flex>
  );
};

export const PipelineFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { partners } = useNotificationsContext();

  const partnerId = searchParams.get('partner') ?? '';
  const side = searchParams.get('side') ?? '';
  const recipient = searchParams.get('recipient') ?? '';

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const partnerOptions: FilterOption[] = [
    { label: 'All', value: '' },
    ...partners.map((p) => ({ label: p.name, value: p.id })),
  ];

  const sideOptions: FilterOption[] = [
    { label: 'All', value: '' },
    { label: 'Buy', value: TransactionSide.BUY },
    { label: 'Sell', value: TransactionSide.SELL },
  ];

  const recipientOptions: FilterOption[] = [
    { label: 'All', value: '' },
    { label: 'Agent', value: ContactType.AGENT },
    { label: 'Loan Officer', value: ContactType.LOAN_OFFICER },
    { label: 'Client', value: ContactType.CLIENT },
  ];

  return (
    <HStack
      alignItems="stretch"
      flexDir={{ base: 'column', md: 'row' }}
      justifyContent="stretch"
      pl={{ base: 0, md: 4, lg: 10 }}
      spacing={0}
    >
      <FilterSelect
        label="Partner"
        options={partnerOptions}
        value={partnerId}
        onChange={(value) => updateFilter('partner', value)}
      />
      <FilterSelect
        label="Side"
        options={sideOptions}
        value={side}
        onChange={(value) => updateFilter('side', value)}
      />
      <FilterSelect
        label="Recipient"
        options={recipientOptions}
        value={recipient}
        onChange={(value) => updateFilter('recipient', value)}
      />
    </HStack>
  );
};
